import React, { useEffect, useMemo, useState } from 'react';
import PropTypes, { string } from 'prop-types';
import style from './MapGlobalContent.module.scss';
import { GoogleMap, Marker, InfoWindow, useLoadScript, MarkerClusterer } from '@react-google-maps/api';

const MapGlobalContent = (props) => {
    const {
        googleMapKey,
        heading,
        mainDescription,
        centerLatitude,
        centerLongitude,
        defaultZoom,
        regions,
        globalSelectButtonText,
        contentTiles,
    } = props;

    const { isLoaded } = useLoadScript({
        id: 'google-map-script',
        googleMapsApiKey: googleMapKey,
    });

    const defaultCenter = {
        lat: centerLatitude,
        lng: centerLongitude,
    };

    const titleList = [globalSelectButtonText, ...regions.map((item) => item.title)];

    const [selectedTab, setSelectedTab] = useState(globalSelectButtonText);
    const [contentData, setContentData] = useState(contentTiles);
    const [selectedTileItem, setSelectedTileItem] = useState(null);
    const [center, setCenter] = useState(defaultCenter);
    const [zoom, setZoom] = useState(defaultZoom);
    const [startIdx, setStartIdx] = useState(0);

    const resetMapToDefault = () => {
        setZoom(defaultZoom);
        setCenter(defaultCenter);
    };

    const updateMap = (zoom, lat, lng) => {
        setZoom(zoom);
        setCenter({ lat, lng });
    };

    useEffect(() => {
        if (selectedTab === globalSelectButtonText) {
            setContentData(contentTiles);
            resetMapToDefault();
        } else {
            const region = regions.find((item) => item.title === selectedTab);
            setContentData(region.contentTiles);
            updateMap(region.defaultZoom, region.centerLatitude, region.centerLongitude);
        }
        setStartIdx(0);
        setSelectedTileItem(null);
    }, [selectedTab]);

    useEffect(() => {
        if (selectedTileItem !== null) {
            if (selectedTileItem) {
                updateMap(selectedTileItem.defaultZoom, selectedTileItem.latitude, selectedTileItem.longitude);
            } else {
                resetMapToDefault();
            }
        }
    }, [selectedTileItem]);
    return (
        <section className={[style.el, 'global-map'].join(' ')}>
            <div className={style.section}>
                <div className={'container'}>
                    <div className={style.title}>
                        <h2>{heading}</h2>
                    </div>
                    <div className={style.description} dangerouslySetInnerHTML={{ __html: mainDescription }} />
                    <div className={style.container}>
                        <div className={style.map}>
                            {isLoaded && (
                                <GoogleMap
                                    mapContainerStyle={{
                                        width: '100%',
                                        minHeight: 500,
                                    }}
                                    options={{
                                        disableDefaultUI: true,
                                    }}
                                    center={center}
                                    zoom={zoom}
                                >
                                    <MarkerClusterer
                                        minimumClusterSize={3}
                                        styles={[
                                            {
                                                url: '/images/map/marker.svg',
                                                height: 40,
                                                width: 40,
                                                textColor: 'white',
                                                textSize: 12,
                                                anchorText: [-2.5, -3],
                                            },
                                        ]}
                                    >
                                        {(clusterer) => (
                                            <>
                                                {contentData.map((item) => (
                                                    <Marker
                                                        key={item.id}
                                                        clusterer={clusterer}
                                                        style={{
                                                            borderRadius: 999,
                                                            overflow: 'hidden',
                                                        }}
                                                        icon={{
                                                            url: '/images/map/marker-2.svg',
                                                        }}
                                                        position={{ lat: item.latitude, lng: item.longitude }}
                                                    >
                                                        {selectedTileItem?.id === item.id && (
                                                            <InfoWindow className={style['info-box']}>
                                                                <img
                                                                    className={style['info-box__logo']}
                                                                    src={item.logo.url}
                                                                    alt={item.logo.alt}
                                                                />
                                                            </InfoWindow>
                                                        )}
                                                    </Marker>
                                                ))}
                                            </>
                                        )}
                                    </MarkerClusterer>
                                </GoogleMap>
                            )}
                        </div>
                        <TabList tabTitleList={titleList} selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
                        <TileContentList
                            contentData={contentData}
                            selectedTileItem={selectedTileItem}
                            setSelectedTileItem={setSelectedTileItem}
                            startIdx={startIdx}
                            setStartIdx={setStartIdx}
                        />
                    </div>
                    <TilePaginate contentData={contentData} startIdx={startIdx} setStartIdx={setStartIdx} />
                </div>
            </div>
        </section>
    );
};

const TileContentList = ({ contentData, setSelectedTileItem, selectedTileItem, startIdx }) => {
    const max = 3;

    const visibleContent = useMemo(() => {
        const slicedContent = contentData.slice(startIdx, startIdx + max);

        while (slicedContent.length < max) {
            slicedContent.push({});
        }

        return slicedContent;
    }, [startIdx, contentData]);

    return (
        <div className={style['tile-content-list']}>
            {visibleContent.map((data, index) =>
                data.id ? (
                    <TileContentItem
                        {...data}
                        key={data.id}
                        setSelectedTileItem={setSelectedTileItem}
                        isActive={data.id === selectedTileItem?.id}
                    />
                ) : (
                    <div className={style['tile-item-placeholder']} key={`placeholder-${index}`} />
                )
            )}
        </div>
    );
};

const TileContentItem = (props) => {
    const { title, description, logo, link, setSelectedTileItem, isActive, sector, location, region, year } = props;
    return (
        <div
            className={[style['tile-item'], isActive && style.active].join(' ')}
            onClick={() => setSelectedTileItem(props)}
        >
            <div className={style['tile-item-left']}>
                {logo?.url && <img src={logo.url} alt={logo?.alt} className={style['tile-item-left__logo']} />}
                <div>
                    {sector && (
                        <p>
                            <strong>{sector}</strong>
                        </p>
                    )}
                    {location && region && (
                        <p>
                            {region} - {location}
                        </p>
                    )}
                    {year && <p>Partner Since {year}</p>}
                </div>
            </div>
            <div className={style['tile-item-right']}>
                <h5 className={style['tile-item-right__title']}>{title}</h5>
                <p className={style['tile-item-right__description']}>{description}</p>
                {link?.link?.url && (
                    <div className={style['tile-item-right__read-more']}>
                        <a href={link.link.url}>{link.text}</a>
                    </div>
                )}
            </div>
        </div>
    );
};

const TabList = ({ tabTitleList, selectedTab, setSelectedTab }) => {
    return (
        <div className={style['tab-list']}>
            {tabTitleList.map((title) => (
                <Tab title={title} isActive={title === selectedTab} setSelectedTab={setSelectedTab} key={title} />
            ))}
        </div>
    );
};

const Tab = ({ title, isActive, setSelectedTab }) => {
    return (
        <div className={[style.tab, isActive && style.active].join(' ')} onClick={() => setSelectedTab(title)}>
            <p>{title}</p>
        </div>
    );
};

const TilePaginate = ({ contentData, startIdx, setStartIdx }) => {
    const max = 3;

    const isHasNext = useMemo(() => startIdx + max < contentData.length, [startIdx, contentData.length]);
    const isHasPrev = useMemo(() => startIdx > 0, [startIdx]);

    const handleNext = () => isHasNext && setStartIdx((prev) => prev + max);
    const handlePrev = () => isHasPrev && setStartIdx((prev) => prev - max);

    return (
        <div className={style['pagination-wrapper']}>
            <div className={style['pagination-info']}>
                <p>
                    {Math.ceil(startIdx / max) + 1} of {Math.ceil(contentData.length / max)}
                </p>
            </div>
            <div className={style.pagination}>
                <button
                    className={[style['button-prev'], !isHasPrev && style.disable].join(' ')}
                    onClick={handlePrev}
                    aria-label="Previous"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect
                            width="24"
                            height="24"
                            transform="matrix(-1 8.74228e-08 8.74228e-08 1 24 0)"
                            fill="white"
                        />
                        <path
                            d="M20 12L4 12M4 12L11.2 5M4 12L11.2 19"
                            stroke="#E67C25"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
                <button
                    className={[style['button-next'], !isHasNext && style.disable].join(' ')}
                    onClick={handleNext}
                    aria-label="Next"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="24" height="24" fill="white" />
                        <path
                            d="M4 12L20 12M20 12L12.8 5M20 12L12.8 19"
                            stroke="#E67C25"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            </div>
        </div>
    );
};
TileContentList.propTypes = {
    contentData: PropTypes.array,
    setSelectedTileItem: PropTypes.func,
    selectedTileItem: PropTypes.object,
    startIdx: PropTypes.number,
    setStartIdx: PropTypes.func,
};

TilePaginate.propTypes = {
    contentData: PropTypes.array,
    startIdx: PropTypes.number,
    setStartIdx: PropTypes.func,
};

TileContentItem.propTypes = {
    title: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    defaultZoom: PropTypes.number,
    description: PropTypes.string,
    logo: PropTypes.object,
    logoSubText: PropTypes.string,
    link: PropTypes.object,
    setSelectedTileItem: PropTypes.func,
    isActive: PropTypes.object,
    sector: PropTypes.string,
    location: PropTypes.string,
    region: PropTypes.string,
    year: PropTypes.string,
};

TabList.propTypes = {
    tabTitleList: PropTypes.arrayOf(string),
    selectedTab: PropTypes.string,
    setSelectedTab: PropTypes.func,
};

Tab.propTypes = {
    title: PropTypes.string,
    isActive: PropTypes.bool,
    setSelectedTab: PropTypes.func,
};

MapGlobalContent.propTypes = {
    heading: PropTypes.string,
    mainDescription: PropTypes.string,
    centerLatitude: PropTypes.number,
    centerLongitude: PropTypes.number,
    defaultZoom: PropTypes.number,
    globalSelectButtonText: PropTypes.string,
    contentTiles: PropTypes.array,
    regions: PropTypes.array,
    googleMapKey: PropTypes.string,
};

export default MapGlobalContent;
